import React, { FC, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { typography } from 'src/config/typography';
import { media } from 'src/config/breakpoints';
import { colors } from 'src/config/colors';
import { AboutUsInfoItem } from 'src/public/components/about-us/AboutUsInfoItem';
import { AboutUsStrengths } from 'src/common/models/aboutUsStaticPage';
import { splitEvery } from 'ramda';
import { AboutUsInfoItemContainer as EmptyAboutUsInfoItem } from 'src/public/components/about-us/AboutUsInfoItemContainer';

const Box = styled.div`
  display: flex;
  flex-direction: column;

  ${media.w.max.px576(css`
    align-items: center;
  `)}
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 1.75rem 5rem;
  width: 100%;

  ${media.w.min.px768(css`
    padding: 0 2.625rem 6.25rem;
  `)}
`;

const Group = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;

  ${media.w.max.px768(css`
    text-align: center;
  `)}
`;

const Heading = styled.div`
  color: ${colors.blue};
  font-family: ${typography.fontFamilyDefault};
  font-size: 1.5rem;
  line-height: 1.875rem;
  margin-top: 1rem;
`;

const List = styled.div`
  display: grid;
  grid-gap: 1.875rem 2.3125rem;
  grid-template-columns: repeat(auto-fill, minmax(14.875rem, 1fr));
  margin-top: 2.375rem;

  ${media.w.between.px768.px992(css`
    grid-template-columns: repeat(auto-fill, minmax(18.875rem, 1fr));
  `)}
`;

const Subtitle = styled.div`
  color: ${colors.pfp3bis};
  font-family: ${typography.fontFamilyDefault};
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  line-height: 1rem;
  text-transform: uppercase;
`;

export interface AboutUsStrengthsSectionProps {
  content: AboutUsStrengths;
}

export const AboutUsStrengthsSection: FC<AboutUsStrengthsSectionProps> = ({ content }) => {
  const items = useMemo(() => splitEvery(2, content.list), [content]);

  return (
    <Container>
      <Box>
        <Header>
          <Subtitle>{content.subtitle}</Subtitle>
          <Heading>{content.heading}</Heading>
        </Header>

        <List>
          {items.map((group, index) => (
            <Group key={index}>
              {group.map((item, index) => (
                <AboutUsInfoItem key={index} text={item.text} unit={item.unit} value={item.value} />
              ))}

              {group.length === 1 && <EmptyAboutUsInfoItem />}
            </Group>
          ))}
        </List>
      </Box>
    </Container>
  );
};
